<template>
  <div v-loading="loading" class="app-main-inner recon-task-detail">
    <el-alert
      v-if="errorMessage"
      :title="errorMessage"
      type="error"
      show-icon
      :closable="false"
      class="recon-task-detail__alert"
    />

    <template v-else-if="detail.taskUuid">
      <div class="section detail-toolbar">
        <div class="detail-toolbar__title">
          <i class="el-icon-document-checked" />
          <span>对账任务详情</span>
        </div>
        <div class="detail-toolbar__actions">
          <el-button size="mini" icon="el-icon-refresh" @click="fetchDetail">刷新</el-button>
          <el-button
            size="mini"
            icon="el-icon-download"
            :disabled="!detail.originalFileId"
            @click="downloadOriginalFile"
          >
            下载原始账单
          </el-button>
          <el-button
            v-if="canReparse"
            size="mini"
            type="primary"
            icon="el-icon-refresh-right"
            @click="handleReparse"
          >
            重新解析
          </el-button>
          <el-button
            v-if="showCompleteAction"
            size="mini"
            type="success"
            icon="el-icon-circle-check"
            :disabled="!canComplete"
            @click="handleComplete"
          >
            对账完成
          </el-button>
        </div>
      </div>

      <div class="section task-summary">
        <div class="task-summary__top">
          <div>
            <div class="task-summary__identity">
              <span class="task-summary__number">{{ detail.taskNo || '-' }}</span>
              <el-tag :type="statusMeta.type" size="mini" effect="plain">
                {{ statusMeta.label }}
              </el-tag>
            </div>
            <div class="task-summary__time">创建于 {{ detail.createdTime || '-' }}</div>
          </div>

          <div class="task-summary__amounts">
            <div class="amount-item">
              <span class="amount-item__label">人民币合计</span>
              <strong>{{ formatAmount(detail.cnyTotal, 'CNY') }}</strong>
            </div>
            <div class="amount-item">
              <span class="amount-item__label">美元合计</span>
              <strong>{{ formatAmount(detail.usdTotal, 'USD') }}</strong>
            </div>
          </div>
        </div>

        <div class="task-summary__grid">
          <div class="summary-field">
            <span>供应商</span>
            <strong>{{ detail.supplierName || '-' }}</strong>
          </div>
          <div class="summary-field">
            <span>账单类型</span>
            <strong>{{ billTypeLabel }}</strong>
          </div>
          <div class="summary-field">
            <span>账单周期</span>
            <strong>{{ periodLabel }}</strong>
          </div>
          <div class="summary-field">
            <span>原始账单</span>
            <el-button
              type="text"
              size="mini"
              :disabled="!detail.originalFileId"
              @click="downloadOriginalFile"
            >
              {{ detail.originalFileName || '-' }}
            </el-button>
          </div>
          <div class="summary-field">
            <span>最近更新</span>
            <strong>{{ detail.updatedTime || '-' }}</strong>
          </div>
          <div class="summary-field summary-field--wide">
            <span>备注</span>
            <strong>{{ detail.remark || '无' }}</strong>
          </div>
        </div>
      </div>

      <div class="section task-progress">
        <div class="section-title">
          <i class="el-icon-s-operation" />
          <span>对账处理进度</span>
        </div>
        <el-steps
          :active="activeStep"
          align-center
          finish-status="success"
          :process-status="progressStatus"
          class="task-progress__steps"
        >
          <el-step title="账单上传" :description="detail.createdTime || '已完成'" />
          <el-step :description="parseStepDescription">
            <div slot="title" class="task-progress__step-title">
              <span>账单解析</span>
              <span
                v-if="parseErrorMessage"
                :data-tooltip="parseErrorMessage"
                :aria-label="parseErrorMessage"
                class="task-progress__error-tip-wrap"
                role="img"
                tabindex="0"
              >
                <i class="el-icon-question task-progress__error-tip" />
              </span>
            </div>
          </el-step>
          <el-step title="系统匹配" :description="matchStepDescription" />
          <el-step title="人工复核" :description="reviewStepDescription" />
          <el-step title="对账完成" :description="completeStepDescription" />
        </el-steps>

        <div v-if="canRematch" class="task-progress__actions">
          <div class="task-progress__action task-progress__action--match">
            <el-button
              type="text"
              size="mini"
              icon="el-icon-refresh-right"
              class="task-progress__rematch"
              :loading="rematching"
              @click="handleRematch"
            >
              重新匹配
            </el-button>
          </div>
        </div>

      </div>

      <div class="metrics-grid">
        <div class="section metric-card metric-card--total">
          <i class="el-icon-tickets metric-card__icon" />
          <div>
            <div class="metric-card__label">总费用条目</div>
            <div class="metric-card__value">
              {{ numberValue(detail.totalItemCount) }} <span>条</span>
            </div>
          </div>
        </div>
        <div class="section metric-card metric-card--matched">
          <i class="el-icon-circle-check metric-card__icon" />
          <div>
            <div class="metric-card__label">完全匹配</div>
            <div class="metric-card__value">
              {{ numberValue(detail.matchedCount) }} <span>条</span>
            </div>
          </div>
        </div>
        <div class="section metric-card metric-card--exception">
          <i class="el-icon-warning-outline metric-card__icon" />
          <div>
            <div class="metric-card__label">异常条目</div>
            <div class="metric-card__value">
              {{ numberValue(detail.exceptionCount) }} <span>条</span>
            </div>
          </div>
        </div>
      </div>

      <div class="section detail-entry">
        <div>
          <div class="detail-entry__title">费用同屏比对</div>
          <div class="detail-entry__desc">
            查看供应商账单与系统应付费用，处理差异、争议和人工接受。
          </div>
        </div>
        <el-button
          type="primary"
          size="small"
          icon="el-icon-s-data"
          :disabled="!canOpenComparison"
          @click="openComparison"
        >
          进入同屏比对详情
        </el-button>
      </div>
    </template>
  </div>
</template>

<script>
import URL from '@/api/urlTranslator'
import { completeReconTask, getReconTaskDetail, preCheckComplete, rematchReconTask, reparseReconTask } from '@/api/clearing/aiReconciliation'

const BILL_TYPE_MAP = {
  FLEET: '干线费用',
  BOOKING: '订舱费用',
  CUSTOMS: '报关费用',
  FBA: '仓储费用',
  OVERSEAS_FLEET: '海外尾程',
  OTHER: '其他费用'
}

const TASK_STATUS_META = {
  CREATED: { label: '已创建', type: 'info', step: 1 },
  PARSING: { label: '解析中', type: '', step: 1 },
  PARSE_FAILED: { label: '解析失败', type: 'danger', step: 1 },
  PARSED: { label: '解析完成', type: 'success', step: 2 },
  MATCHING: { label: '匹配中', type: '', step: 2 },
  MATCH_FAILED: { label: '匹配失败', type: 'danger', step: 2 },
  REVIEWING: { label: '待复核', type: 'warning', step: 3 },
  COMPLETING: { label: '完成中', type: '', step: 4 },
  COMPLETED: { label: '已完成', type: 'success', step: 5 },
  PARTIALLY_COMPLETED: { label: '部分完成', type: 'warning', step: 5 },
  INVALIDATED: { label: '已作废', type: 'info', step: 0 }
}

const PARSE_STATUS_MAP = {
  PENDING: '等待提交',
  SUBMITTED: '已提交，等待解析',
  PARSING: '第三方解析中',
  SUCCESS: '解析成功',
  FAILED: '解析失败',
  TIMEOUT: '解析超时'
}

const COMPARISON_STATUSES = [
  'REVIEWING',
  'COMPLETING',
  'COMPLETED',
  'PARTIALLY_COMPLETED'
]

export default {
  name: 'TabReconciliationResult',
  props: {
    taskId: { type: String, default: '' }
  },
  data() {
    return {
      loading: false,
      rematching: false,
      completing: false,
      errorMessage: '',
      detail: {}
    }
  },
  computed: {
    statusMeta() {
      return TASK_STATUS_META[this.detail.status] || {
        label: this.detail.status || '未知状态',
        type: 'info',
        step: 0
      }
    },
    activeStep() {
      return this.statusMeta.step
    },
    progressStatus() {
      return ['PARSE_FAILED', 'MATCH_FAILED', 'INVALIDATED'].includes(this.detail.status)
        ? 'error'
        : 'process'
    },
    billTypeLabel() {
      return BILL_TYPE_MAP[this.detail.billType] || this.detail.billType || '-'
    },
    periodLabel() {
      const start = this.detail.periodStart
      const end = this.detail.periodEnd
      if (!start) return '-'
      return !end || start === end ? start : `${start} 至 ${end}`
    },
    parseStepDescription() {
      const parseJob = this.detail.currentParseJob || {}
      if (parseJob.finishedTime) return parseJob.finishedTime
      if (parseJob.submittedTime) return parseJob.submittedTime
      return PARSE_STATUS_MAP[parseJob.status] || '等待开始'
    },
    parseErrorMessage() {
      const parseJob = this.detail.currentParseJob || {}
      if (this.detail.status !== 'PARSE_FAILED') return ''
      return parseJob.errorMessage || '账单暂时无法解析，请稍后重新解析或重新上传账单。'
    },
    matchStepDescription() {
      const status = this.detail.status
      if (this.detail.matchCompletedTime) return this.detail.matchCompletedTime
      if (status === 'MATCHING') return '正在匹配财务费用'
      if (status === 'MATCH_FAILED') return '匹配失败，等待人工重新匹配'
      if (COMPARISON_STATUSES.includes(status)) return '匹配批次已生成'
      return '等待解析完成'
    },
    reviewStepDescription() {
      const status = this.detail.status
      if (status === 'REVIEWING') return '等待人工处理差异'
      if (['COMPLETING', 'COMPLETED', 'PARTIALLY_COMPLETED'].includes(status)) return '复核已提交'
      return '等待系统匹配'
    },
    completeStepDescription() {
      if (this.detail.completedTime) return this.detail.completedTime
      if (this.detail.status === 'COMPLETING') return '正在二次校验与锁定'
      return '等待确认'
    },
    canReparse() {
      const parseStatus = this.detail.currentParseJob && this.detail.currentParseJob.status
      return this.detail.status === 'PARSE_FAILED' || ['FAILED', 'TIMEOUT'].includes(parseStatus)
    },
    canRematch() {
      return ['PARSED', 'MATCHING', 'MATCH_FAILED', 'REVIEWING'].includes(this.detail.status)
    },
    showCompleteAction() {
      return ['REVIEWING', 'COMPLETING'].includes(this.detail.status)
    },
    canComplete() {
      return this.detail.status === 'REVIEWING' && this.numberValue(this.detail.exceptionCount) === 0
    },
    canOpenComparison() {
      return COMPARISON_STATUSES.includes(this.detail.status)
    }
  },
  watch: {
    taskId: {
      immediate: true,
      handler() {
        this.fetchDetail()
      }
    }
  },
  methods: {
    fetchDetail() {
      if (!this.taskId) {
        this.errorMessage = '缺少任务标识，无法加载任务详情'
        return
      }
      this.loading = true
      this.errorMessage = ''
      getReconTaskDetail(this.taskId)
        .then(res => {
          this.detail = (res && res.data) || {}
          if (!this.detail.taskUuid) {
            this.errorMessage = '任务详情数据为空'
          }
        })
        .catch(() => {
          this.detail = {}
          this.errorMessage = '任务详情加载失败，请稍后重试'
        })
        .finally(() => {
          this.loading = false
        })
    },
    numberValue(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    },
    formatAmount(value, currency) {
      const number = this.numberValue(value)
      return new Intl.NumberFormat('zh-CN', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(number)
    },
    downloadOriginalFile() {
      if (!this.detail.originalFileId) return
      const sessionId = this.$store.getters.token || ''
      const url = `${URL.common.findByFileId}/${encodeURIComponent(this.detail.originalFileId)}?sessionId=${encodeURIComponent(sessionId)}`
      window.open(url, '_blank')
    },
    handleReparse() {
      this.$confirm('重新解析会重新提交账单文件到解析服务，是否继续？', '重新解析', {
        confirmButtonText: '重新解析',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.loading = true
        return reparseReconTask(this.detail.taskUuid)
          .then(() => {
            this.$message.success('已触发重新解析')
            this.fetchDetail()
          })
          .finally(() => {
            this.loading = false
          })
      }).catch(() => {})
    },
    handleRematch() {
      this.$confirm('重新匹配会重新拉取系统费用并生成新的匹配结果，是否继续？', '重新匹配', {
        confirmButtonText: '重新匹配',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.rematching = true
        return rematchReconTask(this.detail.taskUuid)
          .then(() => {
            this.$message.success('已触发重新匹配')
            this.fetchDetail()
          })
          .finally(() => {
            this.rematching = false
          })
      }).catch(() => {})
    },
    handleComplete() {
      if (!this.canComplete) return
      this.completing = true
      preCheckComplete(this.detail.taskUuid)
        .then(res => {
          const data = (res && res.data) || {}
          if (!data.canComplete) {
            this.$message.warning(data.message || '无法完成对账')
            return
          }
          const lockingCount = data.lockingCount === undefined || data.lockingCount === null
            ? data.completingCount
            : data.lockingCount
          const confirmMsg = data.pendingExceptionCount > 0
            ? `还有 ${data.pendingExceptionCount} 条未处理异常不参与本次结算，确认完成对账？`
            : lockingCount > 0
              ? `确认完成对账？将锁定 ${lockingCount} 条未锁定费用并生成账单`
              : '确认完成对账？费用已在强制接受时锁定，将生成账单'
          this.$confirm(confirmMsg, '提示', { type: 'warning' })
            .then(() => this.doComplete())
            .catch(() => {})
        })
        .finally(() => { this.completing = false })
    },
    doComplete() {
      this.completing = true
      completeReconTask(this.detail.taskUuid)
        .then(res => {
          const data = (res && res.data) || {}
          this.$message.success(data.message || '对账完成')
          this.fetchDetail()
        })
        .finally(() => { this.completing = false })
    },
    openComparison() {
      if (!this.canOpenComparison) {
        this.$message.warning('当前任务尚未进入人工复核阶段')
        return
      }
      this.$emit('open-compare', {
        taskUuid: this.detail.taskUuid,
        taskNo: this.detail.taskNo
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.recon-task-detail {
  min-height: 100%;
  padding-top: 6px;
  display: flex;
  flex-direction: column;
  font-size: 12px;
  line-height: 20px;
}

.recon-task-detail__alert {
  margin-bottom: 5px;
}

.detail-toolbar {
  min-height: 40px;
  flex-direction: row !important;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.detail-toolbar__title,
.section-title {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 700;
  color: #303133;

  i {
    margin-right: 6px;
    color: #409eff;
    font-size: 15px;
  }
}

.detail-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 6px;

  ::v-deep .el-button + .el-button {
    margin-left: 0;
  }
}

.task-summary {
  padding: 12px !important;
}

.task-summary__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #dcdfe6;
}

.task-summary__identity {
  display: flex;
  align-items: center;
  gap: 8px;
}

.task-summary__number {
  font-size: 17px;
  line-height: 24px;
  font-weight: 700;
  color: #303133;
}

.task-summary__time {
  margin-top: 3px;
  color: #909399;
}

.task-summary__amounts {
  display: flex;
  align-items: flex-start;
  gap: 24px;
}

.amount-item {
  min-width: 120px;
  text-align: right;

  strong {
    display: block;
    margin-top: 3px;
    font-size: 16px;
    line-height: 22px;
    color: #303133;
  }
}

.amount-item__label,
.summary-field span,
.metric-card__label,
.detail-entry__desc {
  color: #909399;
}

.task-summary__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px 24px;
  padding-top: 12px;
}

.summary-field {
  min-width: 0;

  span,
  strong {
    display: block;
  }

  strong {
    margin-top: 2px;
    overflow: hidden;
    color: #303133;
    font-weight: 500;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .el-button {
    max-width: 100%;
    margin-top: 2px;
    padding: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.summary-field--wide {
  grid-column: span 3;
}

.task-progress {
  padding: 12px 8px 10px !important;
}

.task-progress__steps {
  margin-top: 18px;

  ::v-deep .el-step__title {
    font-size: 12px;
    line-height: 20px;
  }

  ::v-deep .el-step__description {
    padding: 0 6px;
    font-size: 11px;
    line-height: 16px;
  }
}

.task-progress__step-title {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.task-progress__error-tip {
  color: #f56c6c;
  font-size: 14px;
  vertical-align: -1px;
}

.task-progress__error-tip-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: help;
  outline: none;

  &::after {
    position: absolute;
    z-index: 3000;
    bottom: calc(100% + 8px);
    left: 50%;
    width: max-content;
    max-width: 260px;
    padding: 7px 10px;
    color: #fff;
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
    text-align: left;
    white-space: normal;
    pointer-events: none;
    content: attr(data-tooltip);
    background: rgba(48, 49, 51, 0.94);
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    opacity: 0;
    transform: translate(-50%, 4px);
    visibility: hidden;
    transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s ease;
  }

  &:hover::after,
  &:focus::after {
    opacity: 1;
    transform: translate(-50%, 0);
    visibility: visible;
  }
}

.task-progress__actions {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  margin-top: 4px;
}

.task-progress__action {
  display: flex;
  justify-content: center;
  min-height: 20px;
}

.task-progress__action--match {
  grid-column: 3;
}

.task-progress__rematch {
  padding: 0;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 5px;
}

.metric-card {
  min-height: 70px;
  flex-direction: row !important;
  align-items: center;
  gap: 12px;
  border-left: 4px solid #909399;
}

.metric-card__icon {
  font-size: 28px;
  color: #909399;
}

.metric-card__value {
  margin-top: 3px;
  font-size: 22px;
  line-height: 24px;
  font-weight: 700;
  color: #303133;

  span {
    font-size: 12px;
    font-weight: 400;
    color: #909399;
  }
}

.metric-card--matched {
  border-left-color: #67c23a;

  .metric-card__icon,
  .metric-card__value {
    color: #67c23a;
  }
}

.metric-card--exception {
  border-left-color: #f56c6c;

  .metric-card__icon,
  .metric-card__value {
    color: #f56c6c;
  }
}

.detail-entry {
  min-height: 56px;
  flex-direction: row !important;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.detail-entry__title {
  font-size: 13px;
  font-weight: 700;
  color: #303133;
}

.detail-entry__desc {
  margin-top: 2px;
}

@media (max-width: 1200px) {
  .task-summary__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .summary-field--wide {
    grid-column: span 1;
  }
}

@media (max-width: 768px) {
  .detail-toolbar,
  .task-summary__top,
  .detail-entry {
    flex-direction: column !important;
    align-items: stretch;
  }

  .detail-toolbar__actions,
  .task-summary__amounts {
    flex-wrap: wrap;
  }

  .amount-item {
    min-width: 0;
    text-align: left;
  }

  .task-summary__grid,
  .metrics-grid {
    grid-template-columns: 1fr;
  }

  .task-progress {
    overflow-x: auto;
  }

  .task-progress__steps {
    min-width: 720px;
  }
}
</style>
